import { useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Alert, Box, Button, Field, HStack, Heading, Input, Spinner, Text, VStack } from '@chakra-ui/react'
import axios from 'axios'
import { useQueryClient } from '@tanstack/react-query'
import { grantApi } from '../../lib/api'
import { isNativeApp } from '../../lib/native/platform'
import { clearInstance, getInstance, setInstance } from '../../lib/native/instance'
import { hydrateCredentials } from '../../lib/native/credentialStore'
import { parseInstanceCode } from '../../lib/native/instanceCode'
import { configMatchesOrg, loadPublicConfig, probePublicConfig, PUBLIC_CONFIG_QUERY_KEY } from '../../lib/runtimeConfig'
import { beginOrganizationSwitch, SWITCH_BLOCKED_MESSAGE } from '../../lib/session/organizerSession'
import { storeGrantSessionToken } from '../../lib/session/grantSessionToken'
import { writeGrantMirror } from '../../lib/grantMirror'
import { useGrantSession } from '../../hooks/useGrantSession'
import { TermsAckPanel } from '../../components/canvasser/TermsAckPanel'
import { getReturnCredential, setReturnCredential, returnLinkFor } from '../../lib/returnCredential'
import { SaveReturnCard } from '../../components/enroll/SaveReturnCard'
import { WelcomeBackCard } from '../../components/enroll/WelcomeBackCard'
import type { EnrollResult } from '../../types/grant'

type Step = 'code' | 'profile' | 'saved'

const INVALID_CODE = 'That code is invalid or no longer active. Ask your organizer for the current one.'
const DIFFERENT_ORG = 'This code is for a different organization.'
const ORG_NOT_FOUND = "We couldn't find that organization. Check the ID with your organizer."
const NEEDS_ORG = 'Enter the full code from your organizer, including the organization part.'

function deviceLabel(): string {
  return (navigator.userAgent || 'device').slice(0, 60)
}

/**
 * [L11 Slice 4] Anonymous canvasser self-enrollment against a shared campaign code.
 * A public sibling of RedeemPage (rendered outside CanvasserLayout). Two steps — enter the
 * code (pre-filled from `/join/:code`, which skips straight to the profile step), then the
 * profile (name + optional phone) — then accept terms → minted a grant + session → `/canvasser`.
 *
 * P1 guard: this device may already hold a live grant session (they tapped "join" but were
 * never actually signed out). We check `grant/me` first and offer to continue as that person
 * rather than minting a duplicate identity. Recognising a *truly* signed-out canvasser (an
 * expired/cleared session) needs a personal re-entry credential and is deliberately NOT here —
 * the shared code is a first-touch enrollment funnel and cannot identify an individual. See the
 * P2 backlog note in docs/superpowers/backlog-testing-verification.md.
 */
export function JoinPage() {
  const { code: codeParam } = useParams<{ code?: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const session = useGrantSession()

  const [code, setCode] = useState((codeParam ?? '').toUpperCase())
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [step, setStep] = useState<Step>(codeParam ? 'profile' : 'code')
  const [showTerms, setShowTerms] = useState(false)
  // [CANV-7/8] When the terms gate is showing, remember which flow it belongs to: a personal
  // short-code redeem (resolves an existing grant, no name) or the shared-campaign enroll (mints).
  const [pendingPersonal, setPendingPersonal] = useState(false)
  const [busy, setBusy] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [enrollAnyway, setEnrollAnyway] = useState(false)
  const [savedLink, setSavedLink] = useState<string | null>(null)
  const [campaignLabel, setCampaignLabel] = useState<string | null>(null)
  const termsVersionRef = useRef<string | undefined>(undefined)

  const messageFor = (err: unknown): string => {
    const status = (err as { response?: { status?: number } } | undefined)?.response?.status
    if (status === 404) return INVALID_CODE
    if (status === 409) return 'This code is full — ask your organizer for a new one.'
    if (status === 429) return 'Too many attempts. Please wait a minute and try again.'
    if (axios.isAxiosError(err) && !err.response) return "You're offline. Connect to the internet to join."
    return 'Something went wrong. Please try again.'
  }

  /**
   * [Native B1, NB1-11] A typed code carries its server: `<orgId>-<CODE>`. Returns the code to send
   * (the bare code when prefixed; the typed text unchanged when not), or null after showing why not.
   * - Web: a prefix naming ANOTHER org (per this server's public config) is refused, nothing is
   *   called. An unknown config (null org ID / fetch error) cannot prove a mismatch, so it passes.
   * - Native: a prefix naming another instance is first probed (its public config must exist and
   *   name that org, M1/M3) — a typo signs nobody out and leaves the current instance alone. Only
   *   then is it switched to (NB1-13 — blocked while anything is queued or a grant credential
   *   exists, no silent wipe); a bad org never stays selected. A bare code needs an instance.
   */
  const resolveCode = async (): Promise<string | null> => {
    const parsed = parseInstanceCode(code)
    if (!parsed.code) {
      setFormError(INVALID_CODE)
      setStep('code')
      return null
    }
    if (!parsed.orgId) {
      if (isNativeApp() && !getInstance()) {
        setFormError(NEEDS_ORG)
        setStep('code')
        return null
      }
      return code.trim()
    }
    if (!isNativeApp()) {
      const config = await queryClient.fetchQuery({
        queryKey: PUBLIC_CONFIG_QUERY_KEY, queryFn: loadPublicConfig, staleTime: Infinity,
      })
      if (config?.organizationId && config.organizationId !== parsed.orgId) {
        setFormError(DIFFERENT_ORG)
        setStep('code')
        return null
      }
      return parsed.code
    }
    if (getInstance()?.orgId === parsed.orgId) return parsed.code
    if (!(await probePublicConfig(parsed.orgId))) {
      setFormError(ORG_NOT_FOUND)
      setStep('code')
      return null
    }
    if ((await beginOrganizationSwitch(queryClient)) === 'blocked') {
      setFormError(SWITCH_BLOCKED_MESSAGE)
      setStep('code')
      return null
    }
    await setInstance(parsed.orgId)
    const config = await loadPublicConfig()
    if (!config || !configMatchesOrg(config, parsed.orgId)) {
      await clearInstance()
      setFormError(ORG_NOT_FOUND)
      setStep('code')
      return null
    }
    queryClient.setQueryData(PUBLIC_CONFIG_QUERY_KEY, config)
    await hydrateCredentials()
    return parsed.code
  }

  const doEnroll = async (accept: boolean) => {
    setBusy(true)
    setFormError(null)
    try {
      const sendCode = await resolveCode()
      if (sendCode === null) return
      const { data } = await grantApi.enroll({
        code: sendCode,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim() || undefined,
        acceptTerms: accept || undefined,
        termsVersion: accept ? termsVersionRef.current : undefined,
        deviceLabel: accept ? deviceLabel() : undefined,
      })
      const result = data as EnrollResult
      if ('grantId' in result && result.grantId) {
        const { grantId, kind, subjectContactId, ownerCommitteeId, returnToken } = result
        // [Native B1] Stored before /me below, so that request carries the new gs_ bearer.
        await storeGrantSessionToken(result)
        writeGrantMirror({ grantId, kind, subjectContactId: subjectContactId ?? null, ownerCommitteeId })
        // [Phase 1 / spec §4.1] The session cookie is set now, so /me can name the campaign for the
        // "Continue to {campaign}" button. Best-effort: a failure leaves the plain "Continue".
        void grantApi.me()
          .then(({ data }) => setCampaignLabel(data.campaign?.label ?? null))
          .catch(() => {})
        if (returnToken) {
          try {
            setReturnCredential({ token: returnToken, grantId })
            const link = returnLinkFor(returnToken)
            if (!link) throw new Error('No public origin') // [Native B1] no instance: no link to save
            setSavedLink(link)
            setStep('saved') // show the save-your-way-back card; Continue navigates onward
          } catch {
            // [L11 P2] The grant is already minted and the session cookie is set. If localStorage is
            // unavailable (private mode / quota), do NOT fall into the enroll error path — that would
            // strand an already-enrolled user on the form and invite a duplicate-mint retry. Proceed
            // to the app; re-entry just won't be available on this device.
            navigate('/canvasser', { replace: true })
          }
        } else {
          navigate('/canvasser', { replace: true }) // graceful fallback if no token surfaced
        }
        return
      }
      if ('termsRequired' in result && result.termsRequired) {
        termsVersionRef.current = result.termsVersion ?? undefined
        setShowTerms(true)
        return
      }
      setFormError('Something went wrong. Please try again.')
    } catch (err) {
      const status = (err as { response?: { status?: number } } | undefined)?.response?.status
      setShowTerms(false)
      setFormError(messageFor(err))
      // A bad/expired code is fixed on the code step; a full code is a dead end on either.
      if (status === 404) setStep('code')
    } finally {
      setBusy(false)
    }
  }

  // [CANV-7/8] A typed code is tried as a PERSONAL organizer-minted short code first: it resolves
  // an already-minted grant with no name capture. A 404 means it isn't a personal code, so we fall
  // through to the shared-campaign enroll flow (the profile/name step). This is what stops a
  // pre-named canvasser from being asked for a name — and from landing in the mint funnel, which
  // 404s a personal code as "invalid."
  const doRedeemCode = async (accept: boolean) => {
    setBusy(true)
    setFormError(null)
    try {
      const sendCode = await resolveCode()
      if (sendCode === null) return
      const { data } = await grantApi.redeemCode({
        code: sendCode,
        acceptTerms: accept || undefined,
        termsVersion: accept ? termsVersionRef.current : undefined,
        deviceLabel: accept ? deviceLabel() : undefined,
      })
      if (data.identity) {
        const { grantId, kind, subjectContactId, ownerCommitteeId } = data.identity
        await storeGrantSessionToken(data.identity)
        writeGrantMirror({ grantId, kind, subjectContactId: subjectContactId ?? null, ownerCommitteeId })
        navigate('/canvasser', { replace: true })
        return
      }
      if (data.termsRequired) {
        termsVersionRef.current = data.termsVersion ?? undefined
        setPendingPersonal(true)
        setShowTerms(true)
        return
      }
      // Resolved but neither identity nor terms — shouldn't happen; treat as a shared code.
      setPendingPersonal(false)
      setShowTerms(false)
      setStep('profile')
    } catch (err) {
      const status = (err as { response?: { status?: number } } | undefined)?.response?.status
      setPendingPersonal(false)
      setShowTerms(false)
      if (status === 404) {
        // Not a personal short code — this is (or should be) a shared campaign code. Collect a name.
        setStep('profile')
        return
      }
      // 429 / offline / other: surface it on the code step so the code can be retried.
      setFormError(messageFor(err))
    } finally {
      setBusy(false)
    }
  }

  const continueFromCode = () => {
    if (!code.trim()) {
      setFormError('Enter your access code to continue.')
      return
    }
    setFormError(null)
    void doRedeemCode(false)
  }

  const submitProfile = () => {
    if (!firstName.trim() || !lastName.trim()) {
      setFormError('Enter your first and last name to continue.')
      return
    }
    void doEnroll(false)
  }

  // --- P1: already signed in on this device -------------------------------------------------
  if (session.isLoading)
    return <VStack py={12}><Spinner size="lg" color="accent" /></VStack>

  if (session.identity && !enrollAnyway) {
    const name = [session.identity.subjectFirstName, session.identity.subjectLastName].filter(Boolean).join(' ').trim()
    return (
      <VStack py={12} px={6} gap={5} maxW="26rem" mx="auto" align="stretch">
        <Box>
          <Heading size="lg" mb={1}>You're already signed in{name ? ` as ${name}` : ''}</Heading>
          <Text color="fg.muted">Pick up where you left off, or join a different campaign with a new code.</Text>
        </Box>
        <Button colorPalette="brand" minH="44px" onClick={() => navigate('/canvasser', { replace: true })}>
          {session.identity.campaign?.label ? `Continue to ${session.identity.campaign.label}` : 'Continue'}
        </Button>
        <Button variant="outline" minH="44px" onClick={() => { setEnrollAnyway(true); setStep(codeParam ? 'profile' : 'code') }}>
          Use a different code
        </Button>
      </VStack>
    )
  }

  // --- P2: a stored return credential on this device (no live session) -----------------------
  const returnCred = getReturnCredential()
  if (returnCred && !enrollAnyway && step !== 'saved')
    return (
      <WelcomeBackCard
        token={returnCred.token}
        onUseDifferentCode={() => { setEnrollAnyway(true); setStep(codeParam ? 'profile' : 'code') }}
      />
    )

  // --- Post-enroll: save your way back ------------------------------------------------------
  if (step === 'saved' && savedLink)
    return <SaveReturnCard link={savedLink} campaignLabel={campaignLabel} onContinue={() => navigate('/canvasser', { replace: true })} />

  // --- Terms gate ---------------------------------------------------------------------------
  // [CANV-7/8] The gate serves both flows: a personal short-code redeem or a shared-campaign enroll.
  if (showTerms)
    return <TermsAckPanel onAccept={() => void (pendingPersonal ? doRedeemCode(true) : doEnroll(true))} isSubmitting={busy} />

  // --- Enrollment: step 1 code, step 2 profile ----------------------------------------------
  return (
    <VStack py={12} px={6} gap={6} maxW="26rem" mx="auto" align="stretch">
      <Box>
        <Heading size="lg" mb={1}>Join a campaign</Heading>
        <Text color="fg.muted">
          {step === 'code'
            ? 'Enter the access code from your organizer.'
            : 'Tell us who you are to finish joining.'}
        </Text>
      </Box>

      {!!formError && (
        <Alert.Root status="error" borderRadius="md">
          <Alert.Indicator />
          {formError}
        </Alert.Root>
      )}

      {step === 'code' ? (
        <form onSubmit={(e) => { e.preventDefault(); continueFromCode() }}>
          <VStack gap={4} align="stretch">
            <Field.Root required>
              <Field.Label>Access code</Field.Label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="XXXX-XXXX"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                minH="44px"
              />
            </Field.Root>
            <Button type="submit" colorPalette="brand" width="full" minH="44px" loading={busy}>Continue</Button>
          </VStack>
        </form>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); submitProfile() }}>
          <VStack gap={4} align="stretch">
            <HStack justify="space-between" fontSize="sm" color="fg.muted">
              <Text>Code: <b>{code || '—'}</b></Text>
              <Button variant="plain" size="sm" p={0} h="auto" minH="0" colorPalette="brand"
                onClick={() => { setFormError(null); setPendingPersonal(false); setStep('code') }}>
                Change
              </Button>
            </HStack>
            <Field.Root required>
              <Field.Label>First name</Field.Label>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} minH="44px" />
            </Field.Root>
            <Field.Root required>
              <Field.Label>Last name</Field.Label>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} minH="44px" />
            </Field.Root>
            <Field.Root>
              <Field.Label>Phone (optional)</Field.Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" minH="44px" />
            </Field.Root>
            <Button type="submit" colorPalette="brand" width="full" minH="44px" loading={busy}>Join</Button>
          </VStack>
        </form>
      )}
    </VStack>
  )
}
